import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSearch } from "@fortawesome/free-solid-svg-icons";

interface MarketOption {
  id: number;
  api_code?: string | null;
  api_name?: string | null;
}

interface AttributeMappingSelectProps {
  val: any;
  mappings: Record<string, number>;
  setMappings: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  setPayloadForSave: React.Dispatch<any>;
  dropDownData: MarketOption[];
  fetchOptionsForSearch: (searchTerm: string) => void;
  fetchMoreOptionsForSearch: (searchTerm: string) => void;
  hasMoreOptions: boolean;
  selectedOptionLabels: Record<number, string>;
  setSelectedOptionLabels: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  openDropdownId: number | null;
  setOpenDropdownId: React.Dispatch<React.SetStateAction<number | null>>;
  loading?: boolean;
}

const AttributeMappingSelect: React.FC<AttributeMappingSelectProps> = ({
  val,
  mappings,
  setMappings,
  setPayloadForSave,
  dropDownData,
  fetchOptionsForSearch,
  fetchMoreOptionsForSearch,
  hasMoreOptions,
  selectedOptionLabels,
  setSelectedOptionLabels,
  openDropdownId,
  setOpenDropdownId,
  loading = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOpen = openDropdownId === val.id;
  const getOptionLabel = (option?: MarketOption) =>
    option?.api_name || option?.api_code || "";

  useEffect(() => {
    const currentMappingId = mappings[val.api_name];

    if (currentMappingId) {
      const mappedOption = dropDownData.find((opt) => opt.id === currentMappingId);
      const cachedLabel = selectedOptionLabels[currentMappingId];
      const label = getOptionLabel(mappedOption) || cachedLabel || "";
      setSelectedText(label);
    } else {
      setSelectedText("");
      if (!isOpen) {
        setSearchText("");
      }
    }
  }, [mappings, val.api_name, dropDownData, selectedOptionLabels, isOpen]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId((prev) => (prev === val.id ? null : prev));
        setSearchText(selectedText);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedText, setOpenDropdownId, val.id]);

  const debouncedFetchOptions = useCallback(
    (searchValue: string) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => fetchOptionsForSearch(searchValue), 400);
    },
    [fetchOptionsForSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    setOpenDropdownId(val.id);
    debouncedFetchOptions(value.trim());
  };

  const handleInputClick = () => {
    setOpenDropdownId(val.id);
    if (!searchText.trim()) {
      fetchOptionsForSearch("");
    }
  };

  const handleToggleClick = () => {
    if (isOpen) {
      setOpenDropdownId(null);
      setSearchText(selectedText);
      return;
    }
    setOpenDropdownId(val.id);
    if (!searchText.trim()) {
      fetchOptionsForSearch("");
    }
  };

  const handleOptionClick = (option: MarketOption) => {
    const optionLabel = getOptionLabel(option);
    setSelectedText(optionLabel);
    setSearchText("");                 // clear search box
    setOpenDropdownId(null);
    setSelectedOptionLabels((prev) => ({...prev, [option.id]: optionLabel}));

    setMappings((prev) => ({ ...prev, [val.api_name]: option.id }));

    setPayloadForSave((prev: any) => {
      const existingMappings = prev.mapped_option_value;
      const newMapping = {
        attribute_data_id: option.id,
        platformfielddata_id: val.id,
      };

      const existingIndex = existingMappings.findIndex(
        (item: any) => item.platformfielddata_id === val.id
      );

      if (existingIndex > -1) {
        const updatedMappings = [...existingMappings];
        updatedMappings[existingIndex] = {
          ...updatedMappings[existingIndex],
          attribute_data_id: option.id,
        };
        return { ...prev, mapped_option_value: updatedMappings };
      } else {
        return { ...prev, mapped_option_value: [...existingMappings, newMapping] };
      }
    });
  };

  const filteredOptions =
    dropDownData?.filter((option: MarketOption) =>
      getOptionLabel(option).toLowerCase().includes(searchText.toLowerCase())
    ) || [];

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 24;
    if (!nearBottom || loading || !hasMoreOptions) return;
    fetchMoreOptionsForSearch(searchText.trim());
  };

  return (
    <div className="position-relative w-100" ref={dropdownRef}>
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={selectedText ? `${selectedText}` : "Search or select a value..."}
          value={searchText}
          onChange={handleInputChange}
          onClick={handleInputClick}
          autoComplete="off"
          className="shadow-sm py-2 pe-5 rounded-3 border border-secondary"
        />

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`position-absolute end-0 top-50 translate-middle-y me-3 text-muted transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          style={{cursor: 'pointer'}}
          onClick={handleToggleClick}
        />
      </div>

      {isOpen && (
        <div
          className="border position-absolute bg-white w-100 rounded shadow-sm overflow-auto mt-1 animate__animated animate__fadeIn"
          style={{ zIndex: 20, maxHeight: "200px" }}
          onScroll={handleDropdownScroll}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt: MarketOption) => (
              <OverlayTrigger
                key={opt.id}
                placement="right"
                overlay={<Tooltip id={`tooltip-${opt.id}`}>{getOptionLabel(opt)}</Tooltip>}
              >
                <div
                  className={`py-2 px-3 option-item text-dark text-truncate ${
                    selectedText === getOptionLabel(opt) ? "bg-light fw-semibold border-start border-3 border-primary" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleOptionClick(opt)}
                >
                  {getOptionLabel(opt)}
                </div>
              </OverlayTrigger>
            ))
          ) : (
            <div className="py-3 text-muted text-center">
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              {loading ? "Loading options..." : searchText ? "No results found" : "Start typing to search"}
            </div>
          )}
          {loading && filteredOptions.length > 0 && (
            <div className="py-2 px-3 text-center text-muted small border-top">
              Loading options...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttributeMappingSelect;
